import { Button, InputIcon } from '@platform/ui-library';
import {
  FC,
  SyntheticEvent,
  useMemo
} from 'react';
import { FocusGroup, FormField } from '@app/form_components';
import { useValueForm } from '@app/hooks';
import { ValueOrVariableAutocomplete } from '@app/page-components/shared';
import {
  ArgumentInput,
  FormControlProps,
  ValueOrVariable,
  ValueOrVariableType,
  VariableReferenceScope,
  VariableType,
  VariableValue
} from '@app/types';
import {
  createUuid,
  englishJoin,
  VariableOption
} from '@app/utilities';

type FormValues = {
  argumentName: string;
  id: string;
  valueOrVariable: (
    ValueOrVariable
    & { sensitiveData: boolean; }
  );
};

const defaultValues: ArgumentInput = {
  argumentName: '',
  id: '',
  sensitiveData: false,
  type: ValueOrVariableType.variable,
  varName: ''
};

export type ArgumentInputFormProps = (
  FormControlProps<ArgumentInput>
  & {
    argumentScope?: VariableReferenceScope[];
    onDelete: () => void;
  }
);

export const ArgumentInputForm: FC<ArgumentInputFormProps> = ({
  argumentScope,
  name,
  onBlur,
  onChange,
  onDelete,
  onFocus,
  value
}) => {
  const labels = window.l.form;
  const savedValues = useMemo((): FormValues => {
    const {
      argumentName = defaultValues.argumentName,
      id = defaultValues.id,
      ...valueOrVariable
    } = value ?? defaultValues;

    return {
      argumentName,
      id,
      valueOrVariable
    };
  }, [value]);

  const {
    createSubmitHandler,
    getControlProps
  } = useValueForm<FormValues, ArgumentInput>({
    name,
    savedValues,
    transformValues: (values): ArgumentInput => {
      return {
        argumentName: values.argumentName.trim(),
        id: values.id || createUuid(),
        ...values.valueOrVariable
      };
    },
    validate: ({ argumentName, valueOrVariable }) => {
      argumentName = argumentName?.trim() ?? '';
      const valueOrVariableIsValid = (
        valueOrVariable.type === ValueOrVariableType.value
      ) ? !!valueOrVariable.value : !!valueOrVariable.varName;

      let argumentNameError: string | null = null;
      if (!argumentName) {
        argumentNameError = `"${labels.argument_name}" is required.`;
      } else if (/\s/.test(argumentName)) {
        argumentNameError = `"${labels.argument_name}" must not contain spaces.`;
      } else if (
        argumentScope
        && !argumentScope.some((scope) => {
          const prefix = `${scope}.`;
          return (
            argumentName.startsWith(prefix)
            && argumentName.length > prefix.length
          );
        })
      ) {
        const argumentPrefixes = (
          (argumentScope.length === 1) ? `"${argumentScope[0]}."` : (
            `one of: ${englishJoin(argumentScope.map((s) => `"${s}."`))}`
          )
        );
        argumentNameError = (
          `"${labels.argument_name}" must start with ${argumentPrefixes}`
          + ' & contain an argument name.'
        );
      }

      return {
        argumentName: argumentNameError,
        valueOrVariable: (valueOrVariableIsValid) ? null : (
          `"${labels.argument_value}" is required.`
        )
      };
    }
  });

  const { error: argumentNameFieldError, ...argumentNameField } = getControlProps('argumentName');
  const valueOrVariableField = getControlProps('valueOrVariable');

  const handleValueOrVariableChange = (
    newValue: ValueOrVariable<VariableValue>,
    option: VariableOption | null
  ) => {
    valueOrVariableField.onChange({
      ...newValue,
      sensitiveData: option?.sensitiveData ?? false
    });
  };

  return (
    <FocusGroup onBlur={onBlur} onFocus={onFocus}>
      <form
        key="form"
        className="flex-column flex-gap"
        onSubmit={createSubmitHandler(onChange)}
      >
        <FormField
          error={argumentNameFieldError}
          label={labels.argument_name}
        >
          <InputIcon
            {...argumentNameField}
            onChange={(e: SyntheticEvent<HTMLInputElement>) => {
              argumentNameField.onChange(e.currentTarget.value);
            }}
            placeholder={labels.argument_name_hint}
          />
        </FormField>
        <ValueOrVariableAutocomplete<VariableType>
          {...valueOrVariableField}
          label={labels.argument_value}
          onChange={handleValueOrVariableChange}
          placeholder={labels.argument_value_hint}
          valueType={VariableType.string}
        />
        {(value?.id) ? (
          <div className="flex-gap">
            <Button
              color="warning"
              icon="delete"
              onClick={onDelete}
              theme="outline"
              type="button"
            />
            <Button type="submit">
              {labels.save_argument}
            </Button>
          </div>
        ) : (
          <Button type="submit">
            {labels.add_argument}
          </Button>
        )}
      </form>
    </FocusGroup>
  );
};
